import { __assign, __awaiter, __extends, __generator, __spreadArrays } from "tslib";
import { isDefined } from '@tyler-components-web/core';
import { randomChars } from '../../core';
import { BASE_SELECT_CONSTANTS } from './base-select-constants';
import { isSelectOptionType, SelectOptionType } from './select-utils';
import { ListDropdownAwareFoundation } from '../../list-dropdown/list-dropdown-aware-foundation';
import { POPUP_DIALOG_WIDTH_THRESHOLD } from '../../constants';
var BaseSelectFoundation = /** @class */ (function (_super) {
    __extends(BaseSelectFoundation, _super);
    function BaseSelectFoundation(_adapter) {
        var _this = _super.call(this) || this;
        _this._adapter = _adapter;
        _this._options = [];
        _this._value = [];
        _this._multiple = false;
        _this._open = false;
        _this._selectedValues = [];
        _this._selectedLabels = [];
        _this._selectedIndexes = [];
        _this._filterString = '';
        _this._dialogWidthThreshold = POPUP_DIALOG_WIDTH_THRESHOLD;
        _this._selectOnBlur = false;
        _this._focusListener = function (evt) { return _this._onFocus(evt); };
        _this._blurListener = function (evt) { return _this._onBlur(evt); };
        _this._clickListener = function (evt) { return _this._onClick(evt); };
        _this._keydownListener = function (evt) { return _this._onKeydown(evt); };
        _this._optionsChangedListener = function (options) { return _this._onOptionsChanged(options); };
        _this._activeChangeListener = function (id) { return _this._onActiveOptionChanged(id); };
        _this._dropdownScrollEndListener = function () { return _this._onDropdownScrollEnd(); };
        _this._dismissListener = function () { return _this._onDismiss(); };
        _this._identifier = randomChars();
        return _this;
    }
    BaseSelectFoundation.prototype._onFocus = function (evt) { };
    BaseSelectFoundation.prototype.initialize = function () {
        this._optionListenerDestructor = this._adapter.setOptionsListener(this._optionsChangedListener);
        this._initializeValue();
    };
    BaseSelectFoundation.prototype.initializeTarget = function () {
        this._adapter.initializeAccessibility();
        this._adapter.setMultiple(this._multiple);
        this._adapter.addClickListener(this._clickListener);
        this._adapter.addTargetListener('blur', this._blurListener);
        this._adapter.addTargetListener('focus', this._focusListener);
        this._adapter.addTargetListener('keydown', this._keydownListener);
    };
    BaseSelectFoundation.prototype.disconnect = function () {
        this._adapter.removeClickListener(this._clickListener);
        this._adapter.removeTargetListener('blur', this._blurListener);
        this._adapter.removeTargetListener('focus', this._focusListener);
        this._adapter.removeTargetListener('keydown', this._keydownListener);
        if (this._open) {
            this._closeDropdown();
        }
        if (this._optionListenerDestructor) {
            this._optionListenerDestructor();
        }
    };
    BaseSelectFoundation.prototype.appendOptions = function (options) {
        this._adapter.setOptions(options, false);
        if (this._open) {
            this._adapter.appendDropdownOptions(options);
        }
    };
    BaseSelectFoundation.prototype.selectAll = function () {
        if (this._multiple) {
            this.value = this._flatOptions.map(function (o) { return o.value; });
        }
    };
    BaseSelectFoundation.prototype.deselectAll = function () {
        if (this._multiple) {
            this.value = [];
        }
    };
    Object.defineProperty(BaseSelectFoundation.prototype, "_flatOptions", {
        get: function () {
            if (isSelectOptionType(this._options, SelectOptionType.Group)) {
                return [].concat.apply([], this._options.map(function (g) { return g.options; }));
            }
            return this._options;
        },
        enumerable: true,
        configurable: true
    });
    BaseSelectFoundation.prototype._initializeValue = function () {
        var options = (this._options.length && this._options) || this._adapter.getOptions();
        if (isDefined(this._value) && options.length) {
            this._applyValue(this._value);
        }
    };
    BaseSelectFoundation.prototype._onClick = function (evt) {
        if (evt.button !== 0) {
            return;
        }
        if (!this._open) {
            this._openDropdown();
        }
        else {
            this._closeDropdown();
        }
    };
    BaseSelectFoundation.prototype._onBlur = function (evt) {
        if (this._open) {
            if (this._selectOnBlur && !this._multiple) {
                this._selectActiveOption();
            }
            this._closeDropdown();
        }
    };
    BaseSelectFoundation.prototype._openDropdown = function () {
        var _this = this;
        this._options = this._adapter.getOptions();
        if (!this._flatOptions.length) {
            return;
        }
        this._open = true;
        var config = {
            options: this._options,
            multiple: this._multiple,
            selectedValues: __spreadArrays(this._selectedValues),
            id: this._identifier,
            optionBuilder: this._optionBuilder,
            syncWidth: this._syncPopupWidth,
            highlightFirst: this._highlightFirst && !this._selectOnBlur,
            observeScroll: this._observeScroll,
            observeScrollThreshold: this._observeScrollThreshold,
            scrollEndListener: this._dropdownScrollEndListener,
            activeChangeCallback: this._activeChangeListener,
            targetWidthCallback: this._targetWidthCallback,
            popupClasses: this._popupClasses,
            optionLimit: this._optionLimit,
            headerBuilder: this._popupHeaderBuilder,
            footerBuilder: this._popupFooterBuilder,
            dialogWidthThreshold: this._dialogWidthThreshold,
            closeCallback: function () { return _this._closeDropdown(); },
            selectCallback: function (value) {
                var flatOptions = _this._flatOptions;
                var option = flatOptions.find(function (o) { return o.value === value; });
                if (option) {
                    var index = flatOptions.indexOf(option);
                    _this._onSelect(option, index, true);
                }
            }
        };
        this._adapter.open(config);
        this._adapter.setDismissListener(this._dismissListener);
    };
    /**
     * Closes the dropdown.
     */
    BaseSelectFoundation.prototype._closeDropdown = function () {
        this._open = false;
        this._adapter.close();
    };
    /**
     * Handles selecting an item in the dropdown.
     * @param {ISelectOption} option The selected option.
     * @param {number} optionIndex The index of the selected option.
     */
    BaseSelectFoundation.prototype._onSelect = function (option, optionIndex, closeDropdown) {
        if (closeDropdown === void 0) { closeDropdown = true; }
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve) { return __awaiter(_this, void 0, void 0, function () {
                        var value, label, prevSelectedValues, prevSelectedLabels, prevSelectedIndexes, index, rollbackValue, applyValue, data, shouldContinue, cancelled;
                        var _this = this;
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    if (this._valueChanging) {
                                        return [2 /*return*/, Promise.resolve(false)];
                                    }
                                    value = option ? option.value : '';
                                    label = option ? option.label : '';
                                    prevSelectedValues = __spreadArrays(this._selectedValues);
                                    prevSelectedLabels = __spreadArrays(this._selectedLabels);
                                    prevSelectedIndexes = __spreadArrays(this._selectedIndexes);
                                    if (this._multiple) {
                                        if (this._selectedValues.includes(value)) {
                                            index = this._selectedValues.indexOf(value);
                                            this._selectedValues.splice(index, 1);
                                            this._selectedLabels.splice(index, 1);
                                            this._selectedIndexes.splice(index, 1);
                                        }
                                        else {
                                            this._selectedValues.push(value);
                                            this._selectedLabels.push(label);
                                            this._selectedIndexes.push(optionIndex);
                                        }
                                    }
                                    else {
                                        if (isDefined(value)) {
                                            this._selectedValues[0] = value;
                                            this._selectedLabels[0] = label;
                                            this._selectedIndexes[0] = optionIndex;
                                        }
                                        else {
                                            this._selectedValues = [];
                                            this._selectedLabels = [];
                                            this._selectedIndexes = [];
                                        }
                                    }
                                    rollbackValue = function () {
                                        _this._selectedValues = __spreadArrays(prevSelectedValues);
                                        _this._selectedLabels = __spreadArrays(prevSelectedLabels);
                                        _this._selectedIndexes = __spreadArrays(prevSelectedIndexes);
                                    };
                                    applyValue = function () {
                                        // Always keep the internal value in sync in case the component needs to reinitialize with the existing value later on
                                        _this._value = __spreadArrays(_this._selectedValues);
                                        // If we're in multiselect mode, we need to toggle the selected option
                                        if (_this._multiple) {
                                            var isSelected = _this._selectedIndexes.includes(optionIndex);
                                            _this._adapter.toggleOptionMultiple(optionIndex, isSelected);
                                        }
                                        _this.applySelection();
                                    };
                                    data = this.multiple ? __spreadArrays(this._selectedValues) : this._selectedValues[0];
                                    // We close the dropdown immediately if in single selection mode
                                    if (this._open && closeDropdown && !this._multiple) {
                                        this._closeDropdown();
                                    }
                                    if (!(typeof this._beforeValueChange === 'function')) return [3 /*break*/, 2];
                                    this._valueChanging = Promise.resolve(this._beforeValueChange.call(null, data));
                                    return [4 /*yield*/, this._valueChanging];
                                case 1:
                                    shouldContinue = _a.sent();
                                    this._valueChanging = undefined;
                                    if (!shouldContinue) {
                                        rollbackValue();
                                        return [2 /*return*/, resolve(false)];
                                    }
                                    _a.label = 2;
                                case 2:
                                    cancelled = !this._adapter.emitHostEvent(BASE_SELECT_CONSTANTS.events.CHANGE, data, true, true);
                                    if (!cancelled) {
                                        applyValue();
                                    }
                                    else {
                                        rollbackValue();
                                    }
                                    resolve(!cancelled);
                                    return [2 /*return*/];
                            }
                        });
                    }); })];
            });
        });
    };
    BaseSelectFoundation.prototype._selectActiveOption = function () {
        var activeOptionIndex = this._adapter.getActiveOptionIndex();
        if (activeOptionIndex >= 0 && this._flatOptions[activeOptionIndex]) {
            this._onSelect(this._flatOptions[activeOptionIndex], activeOptionIndex);
        }
    };
    BaseSelectFoundation.prototype._reset = function () {
        this._selectedValues = [];
        this._selectedLabels = [];
        this._selectedIndexes = [];
    };
    BaseSelectFoundation.prototype._applyValue = function (value) {
        var _this = this;
        this._selectedValues = [];
        this._selectedLabels = [];
        this._options = this._adapter.getOptions();
        if (!Array.isArray(value)) {
            value = [value];
        }
        this._value = [];
        var _loop_1 = function (val) {
            if (!this_1._value.includes(val)) {
                this_1._value.push(val);
            }
            var option = this_1._flatOptions.find(function (o) { return o.value === val; });
            if (option) {
                this_1._selectedValues.push(option.value);
                this_1._selectedLabels.push(option.label);
            }
        };
        var this_1 = this;
        // Ensure that the values passed are actually existing options
        for (var _i = 0, value_1 = value; _i < value_1.length; _i++) {
            var val = value_1[_i];
            _loop_1(val);
        }
        // Update the selected indexes based on the values that are now selected
        this._selectedIndexes = this._selectedValues.map(function (val) { return _this._flatOptions.findIndex(function (o) { return o.value === val; }); });
        // Update the selected options in the dropdown
        this._adapter.patchSelectedValues(this._selectedValues);
    };
    /**
     * Handles the user dismissing the dropdown.
     */
    BaseSelectFoundation.prototype._onDismiss = function () {
        this._closeDropdown();
    };
    /** Creates the selected text value from the selected label values. */
    BaseSelectFoundation.prototype._getSelectedText = function () {
        var _this = this;
        if (typeof this._selectedTextBuilder === 'function') {
            var selectedOptions = this._flatOptions.filter(function (o) { return _this._selectedValues.includes(o.value); });
            return this._selectedTextBuilder(selectedOptions);
        }
        if (this._multiple) {
            if (this._selectedLabels.length) {
                return this._selectedLabels.length + " " + (this._selectedLabels.length === 1 ? 'option' : 'options') + " selected";
            }
            else {
                return '';
            }
        }
        return this._selectedLabels
            .filter(function (v) { return v && v.length; })
            .join(' ')
            .trim();
    };
    BaseSelectFoundation.prototype._onKeydown = function (evt) {
        var isEscapeKey = evt.key === 'Escape' || evt.keyCode === 27;
        var isEnter = evt.key === 'Enter' || evt.keyCode === 13;
        var isSpace = evt.key === 'Space' || evt.keyCode === 32;
        var isArrowDown = evt.key === 'ArrowDown' || evt.keyCode === 40;
        var isArrowUp = evt.key === 'ArrowUp' || evt.keyCode === 38;
        var isFilterableCharacter = evt.keyCode >= 48 && evt.keyCode <= 90;
        var isHomeKey = evt.key === 'Home' || evt.keyCode === 36;
        var isEndKey = evt.key === 'End' || evt.keyCode === 35;
        // If an active filter was started, clear it now
        if (!isFilterableCharacter && this._filterTimeout) {
            window.clearTimeout(this._filterTimeout);
            this._filterString = '';
            this._filterTimeout = undefined;
        }
        if (isEscapeKey) {
            evt.preventDefault();
            if (this._open) {
                this._closeDropdown();
                return;
            }
        }
        if (isSpace) {
            evt.preventDefault();
            if (!this._open) {
                this._openDropdown();
            }
            else {
                this._closeDropdown();
            }
        }
        else if (isEnter) {
            evt.preventDefault();
            if (!this._open) {
                this._openDropdown();
            }
            else {
                this._options = this._adapter.getOptions();
                this._selectActiveOption();
            }
        }
        else if (isArrowUp || isArrowDown) {
            evt.preventDefault();
            if (this._multiple && !this._open) {
                this._openDropdown();
                return;
            }
            this._options = this._adapter.getOptions();
            if (this._flatOptions.length === 0) {
                return;
            }
            var optionIndex = 0;
            if (this._open) {
                optionIndex = this._adapter.getActiveOptionIndex();
            }
            else {
                optionIndex = this._getFirstSelectedOptionIndex();
            }
            if (optionIndex < 0) {
                optionIndex = this._getFirstSelectedOptionIndex();
            }
            else {
                if (isArrowUp) {
                    optionIndex = this._getPreviousHighlightableOptionIndex(optionIndex, this._flatOptions);
                }
                else {
                    optionIndex = this._getNextHighlightableOptionIndex(optionIndex, this._flatOptions);
                }
            }
            if (this._open) {
                this._adapter.highlightActiveOption(optionIndex);
            }
            else {
                this._onSelect(this._flatOptions[optionIndex], optionIndex);
            }
        }
        else if (isHomeKey) {
            if (this._open) {
                evt.preventDefault();
                this._adapter.highlightActiveOption(this._flatOptions.findIndex(function (o) { return !o.disabled; }));
            }
        }
        else if (isEndKey) {
            if (this._open) {
                evt.preventDefault();
                var options = this._flatOptions;
                for (var i = options.length - 1; i >= 0; i--) {
                    if (!options[i].disabled) {
                        this._adapter.highlightActiveOption(i);
                        break;
                    }
                }
            }
        }
        else if (isFilterableCharacter) {
            this._filter(evt.key);
        }
    };
    BaseSelectFoundation.prototype._getFirstSelectedOptionIndex = function () {
        var _this = this;
        var selectedOption = this._flatOptions.find(function (option) { return _this._selectedValues.includes(option.value); });
        return selectedOption ? this._flatOptions.indexOf(selectedOption) : 0;
    };
    BaseSelectFoundation.prototype._getPreviousHighlightableOptionIndex = function (startIndex, options) {
        var index = startIndex;
        if (index <= 0) {
            index = options.length - 1;
        }
        else {
            index--;
        }
        if (options[index].disabled) {
            return this._getPreviousHighlightableOptionIndex(index, options);
        }
        return index;
    };
    BaseSelectFoundation.prototype._getNextHighlightableOptionIndex = function (startIndex, options) {
        var index = startIndex;
        if (index === options.length - 1) {
            index = 0;
        }
        else {
            index++;
        }
        if (options[index].disabled) {
            return this._getNextHighlightableOptionIndex(index, options);
        }
        return index;
    };
    BaseSelectFoundation.prototype._filter = function (key) {
        var _this = this;
        // This allows for typing numbers and/or characters while the select is focused to auto-select the closest match
        if (this._filterTimeout) {
            window.clearTimeout(this._filterTimeout);
            this._filterTimeout = undefined;
        }
        this._filterString += key;
        this._filterTimeout = window.setTimeout(function () {
            _this._filterString = '';
            _this._filterTimeout = undefined;
        }, 300);
        this._options = this._adapter.getOptions();
        // TODO(kieran.nichols): Enhance this to cycle through closest matches (see the native select)
        var matchedOption = this._flatOptions.find(function (option) { return !option.disabled && option.label.toLowerCase().startsWith(_this._filterString); });
        if (matchedOption) {
            var optionIndex = this._flatOptions.indexOf(matchedOption);
            if (this._open) {
                this._adapter.highlightActiveOption(optionIndex);
            }
            else if (!this._multiple) {
                this._onSelect(matchedOption, optionIndex, false);
            }
        }
    };
    BaseSelectFoundation.prototype._onOptionsChanged = function (options) {
        this._options = options;
        this._applyValue(this._value);
    };
    BaseSelectFoundation.prototype._onActiveOptionChanged = function (id) {
        this._adapter.updateActiveDescendant(id);
    };
    Object.defineProperty(BaseSelectFoundation.prototype, "value", {
        /** Gets/sets the value of the component. */
        get: function () {
            return this._multiple ? __spreadArrays(this._selectedValues) : this._selectedValues[0];
        },
        set: function (value) {
            var _value;
            if (Array.isArray(value)) {
                _value = __spreadArrays(value);
            }
            else {
                _value = value;
            }
            this._applyValue(_value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "selectedIndex", {
        /** Gets/sets the selected index(s). */
        get: function () {
            return this._multiple ? __spreadArrays(this._selectedIndexes) : this._selectedIndexes[0];
        },
        set: function (indexes) {
            var _this = this;
            this._options = this._adapter.getOptions();
            var indicies;
            if (Array.isArray(indexes)) {
                indicies = __spreadArrays(indexes);
            }
            else {
                indicies = [indexes];
            }
            indicies.sort();
            if (this.multiple) {
                this.value = indicies
                    .map(function (index) { return _this._flatOptions[index]; })
                    .filter(function (o) { return o; })
                    .map(function (o) { return o.value; });
            }
            else {
                var option = this._flatOptions[indicies[indicies.length - 1]];
                if (!option) {
                    return;
                }
                this.value = option.value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "options", {
        /** Gets/sets the available options. */
        get: function () {
            return this._adapter.getOptions();
        },
        set: function (value) {
            var _value;
            if (isSelectOptionType(value, SelectOptionType.Group)) {
                _value = value.map(function (v) { return (__assign({}, v)); });
            }
            else {
                _value = value.map(function (v) { return (__assign({}, v)); });
            }
            this._options = _value;
            this._initializeValue();
            this._adapter.setOptions(_value);
            if (this._open) {
                this._adapter.setDropdownOptions(_value);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "multiple", {
        /** Gets/sets the multiple select state. */
        get: function () {
            return this._multiple;
        },
        set: function (value) {
            if (this._multiple !== value) {
                this._multiple = value;
                this._reset();
                if (this._open) {
                    this._closeDropdown();
                }
                this._adapter.setMultiple(this._multiple);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "open", {
        /** Gets/sets the open state of the dropdown. */
        get: function () {
            return this._open;
        },
        set: function (value) {
            if (this._open !== value) {
                if (value) {
                    this._openDropdown();
                }
                else {
                    this._closeDropdown();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "optionBuilder", {
        /** Gets/sets the builder callback to use when building options. */
        set: function (value) {
            this._optionBuilder = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "selectedTextBuilder", {
        /** Sets the selected text builder callback that will be executed when getting the selected text to display in the field. */
        set: function (fn) {
            this._selectedTextBuilder = fn;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "observeScroll", {
        get: function () {
            return this._observeScroll;
        },
        set: function (value) {
            this._observeScroll = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "observeScrollThreshold", {
        get: function () {
            return this._observeScrollThreshold;
        },
        set: function (value) {
            this._observeScrollThreshold = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "syncPopupWidth", {
        get: function () {
            return this._syncPopupWidth;
        },
        set: function (value) {
            this._syncPopupWidth = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "highlightFirst", {
        get: function () {
            return this._highlightFirst;
        },
        set: function (value) {
            this._highlightFirst = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "optionLimit", {
        get: function () {
            return this._optionLimit;
        },
        set: function (value) {
            this._optionLimit = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "popupClasses", {
        get: function () {
            return this._popupClasses;
        },
        set: function (value) {
            this._popupClasses = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "dialogWidthThreshold", {
        get: function () {
            return this._dialogWidthThreshold;
        },
        set: function (value) {
            this._dialogWidthThreshold = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "popupHeaderBuilder", {
        set: function (value) {
            this._popupHeaderBuilder = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "popupFooterBuilder", {
        set: function (value) {
            this._popupFooterBuilder = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "popupElement", {
        /** Gets the currently active popup element when the dropdown is open. */
        get: function () {
            return this._adapter.popupElement;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "beforeValueChange", {
        /** Sets the callback to be executed when the user selects a value. */
        set: function (value) {
            this._beforeValueChange = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseSelectFoundation.prototype, "selectOnBlur", {
        get: function () {
            return this._selectOnBlur;
        },
        set: function (value) {
            if (this._selectOnBlur !== value) {
                this._selectOnBlur = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    return BaseSelectFoundation;
}(ListDropdownAwareFoundation));
export { BaseSelectFoundation };
